import cx from 'classnames'
import Banner from 'common/Banner/Banner'
import BannerContent from 'common/BannerContent/BannerContent'
import { useArticleReviewSections } from 'common/Prose/hooks/useArticleReviewSections'
import { MobileToolbarProvider } from 'common/Prose/Toolbar/MobileToolbar/stores/MobileToolbarStore'
import CopiedCoreTemplateModal from 'components/CopiedCoreTemplateModal/CopiedCoreTemplateModal'
import { DocObservers } from 'components/DocObservers/DocObservers'
import { useLayers } from 'hooks/queries/layers/useLayers'
import { useAuth } from 'hooks/UseAuth'
import { useBrand } from 'hooks/useBrand'
import { useDebouncedResizeObserver } from 'hooks/useDebouncedResizeObserver'
import { useFadingScrollbar } from 'hooks/useFadingScrollbar'
import { useFeatureFlagUserGroup } from 'hooks/useFeatureFlagUserGroup'
import { useInformalFeedbackRequestRealtimeChannel } from 'hooks/useInformalFeedbackRequestRealtimeChannel'
import { useInformalFeedbackRequestRealtimeChannel as useInformalFeedbackRequestAlmanacFilesRealtimeChannel } from 'hooks/almanacFiles/useInformalFeedbackRequestRealtimeChannel'
import { useOnboardingLoader } from 'hooks/useOnboardingLoader'
import { useQueryParams } from 'hooks/useQueryParams'
import { useScrollInput } from 'hooks/useScrollInput'
import { useSuggestionsRealtimeNotifications } from 'hooks/useSuggestionsRealtimeNotifications'
import { useUnregisteredUserBanner } from 'hooks/useUnregisteredUser'
import { isEqual } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useProseInstance } from 'providers/ProseInstanceProvider'
import { useContext, useEffect, useRef, useState } from 'react'
import Helmet from 'react-helmet'
import { useSelector, useStore } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { DocPresenceContext } from 'stores/DocPresence'
import { useDocStore } from 'stores/DocStore'
import { DocMode } from 'stores/DocStore.types'
import { ArticleFlat, ArticleView, RootState, UserRole } from 'types'
import { isExternalResource } from 'utils/articleUtils'
import { USERS } from 'utils/featureFlagUserGroups'
import useTemplatePicker from '../../hooks/templates/useTemplatePicker'
import { OFFLINE_OVERLAY_CONTAINER_ID } from './components/RealtimeProvider/OfflineOverlay'
import { TOOLBAR_OVERLAY_CONTAINER_ID } from './components/RealtimeProvider/ToolbarOverlay'
import { HIDDEN_INPUT_ID, MAIN_CONTENT_ID, TOOLBAR_CONTAINER_ID } from './Constants'
import Styles from './ProseEdit.module.scss'
import { VcmToastMessageContainer } from './components/VcmToastMessage/VcmToastMessage'
import { ExternalResourceEditor } from './components/ExternalResourceEditor/ExternalResourceEditor'
import { ArticleEditor } from './components/ArticleEditor/ArticleEditor'
import { Can } from 'components/Can'
import { useWorkspace } from 'hooks/useWorkspace'
import { useRootStore } from 'stores/RootStore'
import { SubjectType } from 'utils/caslAbility'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import * as Layout from 'components/Layout'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { useInformalFeedbackRequests } from 'hooks/almanacFiles/useInformalFeedbackRequests'

interface Props {
  article: ArticleFlat
  layerId: string
  withCommentViewOnly?: boolean
  canEdit?: boolean
}

const ProseEdit = observer(({ article, layerId, withCommentViewOnly, canEdit }: Props) => {
  const auth = useAuth()
  const workspace = useWorkspace(article.company_id)

  /*
      TODO:
      Update ArticleViewWrapper to use useArticle instead of local state.
      useArticle is used here because we need up-to-date info to check if the article is locked.
      Once ArticleViewWrapper is refactored, we can remove this.
    */
  const store = useStore<RootState>()
  const history = useHistory()
  const location = useLocation()
  const { name: brandName } = useBrand()
  const canObserveUsers = useFeatureFlagUserGroup([USERS.withAlmanacEmail])
  const [copiedCoreTemplateModalVisible, setCopiedCoreTemplateModalVisible] = useState(false)
  const [showUnregisteredBanner] = useUnregisteredUserBanner()
  const docPresenceState = useContext(DocPresenceContext)
  const { ref: rootContainerRef, width: rootWidth } = useDebouncedResizeObserver<HTMLDivElement>(50)
  const { hideOnboardingLoader } = useOnboardingLoader()
  const { currentViewRef } = useProseInstance()
  const { data } = useLayers({ articleKey: article.id })
  const layer = data?.layers.find((layer) => layer.id === layerId)
  const docStore = useDocStore()
  const rootStore = useRootStore()
  const { isDesktop, isMobile } = useResponsiveDesign()
  const { isChannelSocialPostable } = useIsChannelPostable()

  const { focusMode, articleMeta, onboardingLoaderVisible } = useSelector((state: RootState) => {
    return {
      articleMeta: state.articleMeta,
      focusMode: state.focusMode,
      onboardingLoaderVisible: state.onboardingLoaderVisible,
    }
  }, isEqual)

  const params = useQueryParams()

  useEffect(() => {
    if (onboardingLoaderVisible) {
      hideOnboardingLoader()
    }
  }, [onboardingLoaderVisible, hideOnboardingLoader])

  const mainContainer = useRef<HTMLElement>(null)
  useFadingScrollbar(mainContainer.current)

  useEffect(() => {
    if (!params.coreTemplateCopy) return

    setCopiedCoreTemplateModalVisible(true)

    const queryParams = new URLSearchParams(location.search)
    queryParams.delete('coreTemplateCopy')
    history.replace({ search: queryParams.toString() })
  }, [params.coreTemplateCopy, history, location.search])

  useScrollInput(() => {
    if (docPresenceState && docPresenceState.followedUser.user) {
      docPresenceState.followedUser.user = null
      docPresenceState.followedUser.meta = null
    }
  })

  useEffect(() => {
    if (currentViewRef.current) {
      const { state, dispatch } = currentViewRef.current
      const { tr } = state
      tr.setMeta('follow_user', docPresenceState?.followedUser.user)
      dispatch(tr)
    }
  }, [docPresenceState?.followedUser.user, currentViewRef])

  const [leftRailOpen, setLeftRailOpen] = useState<boolean>(false)
  useEffect(() => {
    if (rootWidth == null) return
    setLeftRailOpen(window.innerWidth - rootWidth > 0)
  }, [rootWidth])

  useEffect(() => {
    if (!isDesktop) {
      const activeTab = rootStore.docStore?.rightRailStore.activeTab

      rootStore.layout.setRightColumnLocked(false)
      rootStore.docStore?.rightRailStore.setActiveTab(activeTab)
    }
  }, [isDesktop, rootStore])

  const closeCoreTemplateCopyModal = () => {
    setCopiedCoreTemplateModalVisible(false)
  }

  const pageTitle = articleMeta[article.id]?.title || article.title
  const { modal: suggestionRealtimeNotificationModal } = useSuggestionsRealtimeNotifications(auth?.id, article.id)
  const informalFeedbackRequests = useInformalFeedbackRequests(article.almanac_file.id, { staleTime: 0 })
  useInformalFeedbackRequestRealtimeChannel(article.id, layerId) // Remove when reviews as sharing is released
  useInformalFeedbackRequestAlmanacFilesRealtimeChannel(article.almanac_file.id)

  useTemplatePicker(article)

  useArticleReviewSections(informalFeedbackRequests)

  const isArticleExternalResource = isExternalResource(article)

  const docModeIsEditor = docStore.docMode === DocMode.Editor

  if (!layer) {
    return null
  }

  const file = rootStore.files.get(article.id)
  const authedArticle = {
    role: workspace?.role || UserRole.UNAUTHED,
    permissions: file?.permissions,
    __caslSubjectType__: SubjectType.ARTICLE,
  }

  const shouldHideContainer = () => {
    if (!isChannelSocialPostable(article.channel)) {
      return false
    }

    if (isMobile && ![UserRole.UNAUTHED].includes(authedArticle.role)) {
      return true
    }

    return false
  }

  return (
    <>
      <Can I="createLayer" a={authedArticle} inWorkspace={article.company_id}>
        <VcmToastMessageContainer articleKey={article.id} layer={layer} />
      </Can>
      <div
        ref={rootContainerRef}
        className={cx(Styles.container, Styles[docStore.articleView ?? ArticleView.Reading], {
          [Styles.withUnregisteredBanner]: showUnregisteredBanner,
          [Styles.isExternalFile]: isArticleExternalResource,
          [Styles.leftRailOpen]: leftRailOpen,
          [Styles.leftRailClosed]: !leftRailOpen,
          [Styles.notInEditorMode]: !docModeIsEditor,
          [Styles.hidden]: shouldHideContainer(),
        })}
      >
        <Helmet defer={false}>
          <title>
            {pageTitle} - {brandName}
          </title>
        </Helmet>

        {!isArticleExternalResource && (
          <div
            id={TOOLBAR_CONTAINER_ID}
            className={cx(Styles.nerudaToolbarContainer, { [Styles.notInEditorMode]: !docModeIsEditor })}
          />
        )}

        <div id={TOOLBAR_OVERLAY_CONTAINER_ID} className={Styles.nerudaToolbarContainer} />
        <section
          className={cx(Styles.content, {
            [Styles.focusMode]: focusMode.showFocusMode,
            [`focusMode`]: focusMode.showFocusMode,
            [Styles.notInEditorMode]: !docModeIsEditor,
            [Styles.isBlazeEditor]: !article.external_link,
          })}
        >
          <main
            ref={mainContainer}
            id={MAIN_CONTENT_ID}
            className={cx(Styles.main, Styles.noRightRail, {
              [Styles.externalResource]: isArticleExternalResource,
            })}
            style={
              docPresenceState?.followedUser.meta
                ? { border: `4px solid ${docPresenceState.followedUser.meta.color}` }
                : undefined
            }
            data-layout={store.getState()['articleMeta'][article.id]?.layout ?? 'fixed'}
          >
            {canObserveUsers && !isArticleExternalResource && (
              <>
                {!!docPresenceState?.followedUser?.user && (
                  <div
                    className={cx(Styles.observingTag, Styles.toTheRight)}
                    style={{ background: docPresenceState.followedUser.meta?.color }}
                  >
                    Observing {docPresenceState.followedUser.user.first_name}{' '}
                    {docPresenceState.followedUser.user.last_name}
                  </div>
                )}
                <div className={cx(Styles.docObserversContainer, Styles.toTheRight)}>
                  <DocObservers />
                </div>
              </>
            )}
            {article.is_stub && (
              <Banner className={Styles.banner}>
                <BannerContent
                  heading="For requesters"
                  text="Add details about what you're looking for. Common details include what you'll use the doc for and questions you need answered in the doc."
                />
              </Banner>
            )}
            <div
              className={cx(Styles.belowActionBarContainer, { [Styles.externalResource]: isArticleExternalResource })}
            >
              <div className={Styles.articleContentsContainer}>
                {isArticleExternalResource && <ExternalResourceEditor article={article} />}
                {!isArticleExternalResource && (
                  <ArticleEditor
                    article={article}
                    canEdit={canEdit}
                    layer={layer}
                    withCommentViewOnly={withCommentViewOnly}
                    leftRailOpen={leftRailOpen}
                  />
                )}
              </div>
            </div>
          </main>
          {suggestionRealtimeNotificationModal}
          {copiedCoreTemplateModalVisible && <CopiedCoreTemplateModal close={closeCoreTemplateCopyModal} />}
        </section>
        <Layout.MobileWriterToolbarContainer />
      </div>
      <div id={OFFLINE_OVERLAY_CONTAINER_ID} />
      {!isDesktop && <input type="text" className={Styles.hiddenInput} id={HIDDEN_INPUT_ID} />}
    </>
  )
})
ProseEdit.displayName = 'ProseEdit'

export interface LoadProps {
  article: ArticleFlat
  layerId: string
  withCommentViewOnly?: boolean
  canEdit?: boolean
}

const LoadWrapper = ({ article, layerId, withCommentViewOnly, canEdit }: LoadProps) => {
  return (
    <MobileToolbarProvider>
      <ProseEdit canEdit={canEdit} article={article} layerId={layerId} withCommentViewOnly={withCommentViewOnly} />
    </MobileToolbarProvider>
  )
}

export default LoadWrapper
